import React, { useEffect, useState, useRef } from "react";
// nodejs library that concatenates classes
import classNames from "classnames";
import * as Fathom from "fathom-client";
import { useHistory } from "react-router-dom";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  CircularProgress,
  FormHelperText,
  FormControl,
} from "@material-ui/core";

// core components
import Header from "components/Header/Header.js";
import Footer from "components/Footer/Footer.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Parallax from "components/Parallax/Parallax.js";

import styles from "assets/jss/material-kit-react/views/landingPage.js";

// Sections for this page
import useCareCounter from "useCareCounter";
import { accessDocument } from "../../prismicApi.js";
import LanguageIcon from "@material-ui/icons/Language";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import EmailSection from "./Sections/EmailSection";
import BubbleControlSection from "./Sections/BubbleControlSection";
import AboutSection from "./Sections/AboutSection";
import { ShareButtons } from "components/ShareButtons/ShareButtons.js";

const dashboardRoutes = [];

const useStyles = makeStyles(styles);

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 769);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 769);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
}

const LandingPage = ({ ...rest }) => {
  const classes = useStyles();
  const [doc, setDoc] = useState(null);
  const isPathEnglish = window.location.pathname.includes('/en');
  const [english, setEnglish] = useState(isPathEnglish === 'en');
  const [doCareSpinner, setDoCareSpinner] = useState(false);
  const isMobile = useIsMobile();
  const [isEmailModalOpen, setEmailModalOpen] = useState(false);
  const { careText, cared, doCare, careError } = useCareCounter({ english });
  const emailRef = useRef();
  const headerRef = useRef();
  const history = useHistory();

  //change html lang tag to english
  useEffect(() => {
    document
      .getElementsByTagName("html")[0]
      .setAttribute("lang", english ? "en" : "no");
  }, [english]);

  const handleLanguageClick = () => {
    Fathom.trackGoal("TPWXQVLG", 0);
    if (english) {
      history.push("/oljefondet");
    } else {
      history.push("/oljefondet/en");
    }
    setEnglish(!english);
  };

  const scrollToElement = (element) => {
    const headerHeight = 200;
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;
    const scrollPosition = elementPosition - headerHeight;

    window.scroll({
      left: 0,
      top: scrollPosition,
      behavior: "smooth",
    });
  };

  const scrollToEmailSection = () => {
    scrollToElement(emailRef.current);
  };

  useEffect(() => {
    Fathom.load("ELYFLNBD");
    accessDocument("company_article").then((result) => setDoc(result));
  }, []);

  const onClick = async () => {
    if (cared) {
      if (isMobile) {
        setEmailModalOpen(true);
      } else {
        scrollToEmailSection();
      }
    } else {
      try {
        // Track the goal and show the spinner
        Fathom.trackGoal("BEMVKBGJ", 0);
        setDoCareSpinner(true);

        // Prepare the care action and a timer for a 2-second delay
        const carePromise = doCare();
        const timerPromise = new Promise((resolve) =>
          setTimeout(resolve, 2000)
        );

        // Wait for both the care action and the timer to complete
        await Promise.all([carePromise, timerPromise]);

        // Open the modal after the actions are complete
        setEmailModalOpen(true);
      } catch (e) {
        console.error("Error during care action: ", e);
      } finally {
        // Hide the spinner after the process is complete
        setDoCareSpinner(false);
      }
    }
  };

  return (
    <div>
      <div style={{ backgroundColor: "black" }}>
        <Header
          forwardedRef={headerRef}
          color="dark"
          routes={dashboardRoutes}
          brand="Norsk Folkehjelp"
          scrollToElement={scrollToElement}
          english={english}
          mobileLanguageButton={
            <div style={{ display: "inline-flex" }}>
              <Button color="info" size="sm" onClick={handleLanguageClick}>
                <LanguageIcon />
                {english ? "Norsk" : "English"}
              </Button>
            </div>
          }
          rightLinks={
            <div style={{ display: "inline-flex" }}>
              <ShareButtons english={english} />
              <Button color="info" size="sm" onClick={handleLanguageClick}>
                <LanguageIcon />
                {english ? "Norsk" : "English"}
              </Button>
            </div>
          }
          fixed
          {...rest}
        />
        <div className={classes.parallaxContainer}>
          <Parallax filter image={require("assets/img/mann.jpg")}>
            <div
              className={classes.container}
              style={{ marginTop: "100px", marginBottom: "100px" }}
            >
              <GridContainer>
                <GridItem xs={12} sm={12} md={6}>
                  <h1 className={classes.title}>
                    {english
                      ? "The Norwegian oil fund is investing in the occupation of Palestine"
                      : "Oljefondet investerer i okkupasjonen av Palestina"}
                  </h1>
                  <h2 className={classes.careText}>{careText}</h2>
                  <br />
                  <div className={classes.careAndShareButtons}>
                    <Button
                      color="danger"
                      size="lg"
                      onClick={onClick}
                      id={"openModalButton"}
                    >
                      <MailOutlineIcon style={{ marginRight: "12px" }} />
                      {english ? "Yes, I care" : "Ja, jeg bryr meg!"}
                    </Button>
                    {doCareSpinner && (
                      <CircularProgress
                        size="3em"
                        className={classes.careButtonProgress}
                      />
                    )}
                  </div>
                  <FormControl error={!!careError}>
                    <FormHelperText
                      style={{ alignSelf: "center", fontSize: "1.2em" }}
                    >
                      {careError || " "}
                    </FormHelperText>
                  </FormControl>
                </GridItem>
              </GridContainer>
            </div>
          </Parallax>
        </div>
        <div className={classes.staticImageContainer}>
          <figure>
            <img
              src={require("assets/img/mann.jpg")}
              alt={
                english
                  ? "An old man staring into the camera"
                  : "En gammel mann som stirrer inn i kameraet"
              }
            />
            <figcaption className={classes.aboutImgCaption}>
              {english ? "Photo: " : "Foto:"} Ingunn Eriksen
            </figcaption>
          </figure>
          <div className={classes.mobileHeroInfo}>
            <h1 className={classes.title}>
              {english
                ? "The Norwegian oil fund is investing in the occupation of Palestine"
                : "Oljefondet investerer i okkupasjonen av Palestina"}
            </h1>
            <h2 className={classes.careText}>{careText}</h2>
            <br />
            <div className={classes.careAndShareButtons}>
              <Button
                color="danger"
                size="med"
                onClick={onClick}
                style={{ padding: "0.55rem 1.25rem" }}
              >
                <MailOutlineIcon style={{ marginRight: "6px" }} />
                {english ? "Yes, I care" : "Ja, jeg bryr meg!"}
              </Button>
              {doCareSpinner && (
                <CircularProgress
                  size="3em"
                  className={classes.careButtonProgress}
                />
              )}
              <ShareButtons english={english} />
            </div>
            <FormControl error={!!careError}>
              <FormHelperText
                style={{ alignSelf: "center", fontSize: "1.2em" }}
              >
                {careError || " "}
              </FormHelperText>
            </FormControl>
          </div>
        </div>
        <div className={classNames(classes.main)} ref={emailRef}>
          {isMobile ? (
            isEmailModalOpen && (
              <EmailSection
                english={english}
                isOpen={isEmailModalOpen}
                onClose={() => setEmailModalOpen(false)}
              />
            )
          ) : (
            <EmailSection english={english} />
          )}
          <BubbleControlSection
            doc={doc}
            english={english}
            scrollToElement={scrollToElement}
            id={"hvilke-selskaper-er-involvert"}
          />
          <AboutSection
            section="om_okkupasjonen"
            english={english}
            ids={[
              "om_okkupasjonen",
              "om_kampanjen",
              "om_oljefondet",
              "hva_sier_partiene",
            ]}
          />
        </div>

        <Footer english={english} />
      </div>
    </div>
  );
};

export default LandingPage;
